import { format as formatUtil } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';
/**
 * Turns a date or timestamp into a shiny string.
 * @param value A Date object or timestamp.
 * @param format The way you want it to look (we've got presets, so no wild formats allowed).
 * @returns A nicely formatted time or date string.
 */
export function formatDate(value, format) {
    return formatUtil(new Date(value), format);
}
/**
 * Formats duration in seconds to hh:mm:ss or mm:ss if hours is 0
 */
export function formatDuration(seconds) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    const formattedMinutes = minutes.toString().padStart(2, '0');
    const formattedSeconds = remainingSeconds.toString().padStart(2, '0');
    if (hours > 0) {
        return `${hours}:${formattedMinutes}:${formattedSeconds}`;
    }
    return `${formattedMinutes}:${formattedSeconds}`;
}
/**
 * Hook that gives you a flag that clears itself after a timeout (ttl).
 * @param initiallyRaised Whether it starts off raised (true).
 * @param ttl Time to keep the flag raised.
 */
export function useTimedFlag(initiallyRaised, ttl) {
    const [isRaised, setIsRaised] = useState(initiallyRaised);
    const timeoutRef = useRef();
    const raise = useCallback(() => {
        setIsRaised(true);
        if (timeoutRef.current) {
            window.clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = window.setTimeout(() => {
            setIsRaised(false);
            timeoutRef.current = undefined;
        }, ttl);
    }, [ttl]);
    useEffect(() => {
        if (initiallyRaised) {
            timeoutRef.current = window.setTimeout(() => {
                setIsRaised(false);
                timeoutRef.current = undefined;
            }, ttl);
        }
        return () => {
            if (timeoutRef.current) {
                window.clearTimeout(timeoutRef.current);
            }
        };
    }, [initiallyRaised, ttl]);
    return [isRaised, raise];
}
